import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ApplicationService } from "@app/applications/application.service";
import { MatCheckboxChange, MatSnackBar } from "@angular/material";
import { ActivatedRoute } from "@angular/router";
import { takeUntil } from "rxjs/operators";
import { componentDestroyed } from "@w11k/ngx-componentdestroyed";
import { OnDestroy } from "@angular/core";
import { Applicant } from "@app/applications/application-details/collateral/collateralDetails/collateral-details.model";
import { DocumentVerificationDTO } from "@app/applications/application-details/business/kycProofs/kyc.model";
import { BureauReport } from "@app/applications/application-details/applicant/bureau-reports/bureau-reports.model";
import { BureauResponse } from "../xdata-response.model";
import { ValidationService } from "@app/applications/application-validators/validation.service";
import { Liability } from "@app/applications/application-details/financials/liability/liability.model";
@Component({
  selector: "eng-fetch-liability",
  templateUrl: "fetch-liability.template.html",
  styleUrls: [
    "../../../application-details.styles.scss",
    "../xdata-service.style.scss",
  ],
})
export class LiabilityFetchComponent implements OnInit, OnDestroy {
  applicationUid: string;

  xDataServicesForm: FormGroup;
  applicants: Applicant[];
  serviceProviderCode: string;
  bureauReports: BureauReport[] = [];
  bureauResponse: BureauResponse;
  documentType: string;
  listOfLiabilitySupportBureauReport: BureauReport[] = [];
  isShowResultCard: boolean;
  verificationDto: DocumentVerificationDTO = {};
  isCheckboxSelected: boolean;
  selectedLiabilities: boolean[] = [];
  selectedExistingLiabilities: boolean[] = [];
  existingLiabilities: Liability[] = [];
  activeLiabilities: Liability[] = [];
  closedLiabilities: Liability[] = [];
  selectedClosedLiabilities: boolean[] = [];
  selectedLoanLiabilities: boolean[] = [];
  selectedCreditCardLiabilities: boolean[] = [];
  loanLiabilities: Liability[] = [];
  creditCardLiabilities: Liability[] = [];
  allLiabilities: Liability[];
  showError: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private applicationService: ApplicationService,
    private _snackbar: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private validationService: ValidationService
  ) {
    this.xDataServicesForm = this.formBuilder.group({
      borrower: ["", Validators.required],
      reportType: ["", Validators.required],
    });
    this.activatedRoute.queryParams
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((params) => {
        this.applicationUid = params.applicationUid;
        this.serviceProviderCode = params.xDataService;
      });
  }

  ngOnInit() {
    this.getApplicants();
    this.getBureauReports();
  }


  getApplicants() {
    this.applicationService
      .getApplicants(this.applicationUid)
      .subscribe((response: any) => {
        if (response) {
          this.applicants = response;
        }
      });
  }

  onInitiateClick() {
    this.resetValuesToAvoidDuplicate();
    this.validationService.markFormGroupTouched(this.xDataServicesForm)
    if (this.xDataServicesForm.valid) {
      let bureaureportuid = this.xDataServicesForm.controls.reportType.value;
      this.applicationService.liabilityFetch(this.applicationUid, bureaureportuid).toPromise().then((_success) => {
        this.bureauResponse = _success.body;
        this.isShowResultCard = true;
        this.allLiabilities = this.bureauResponse.liabilities;
        if (this.bureauResponse == null || this.allLiabilities == null || this.allLiabilities.length === 0) {
          this.isShowResultCard = false;
          this.showError = true;
          return;
        }
        // if writeOffAmount, overdueAmount and currentOutstanding if any of the field has positive value then it is considered as active
        // there is no values in these fields are more likely to consider as there are not pending payments so we are moving to closed liability
        this.allLiabilities.forEach(liability => {
          liability.applicantOptions = [];
          liability.displayedApplicantName = '';

          // MARK LIABILITY TO: We need to show only the applicant for whom the liability is linked
          // and the applicants for whom the liability fetch was initiated.
          // Therefore, we update `applicantOptions` with these values instead of showing all applicants in the dropdown.
          // Both `applicantOptions` and `displayedApplicantName` are client-side properties
          // added purely for UI purposes and are not returned from the server.
          this.applicants.forEach(applicant => {
            if (this.xDataServicesForm.controls.borrower.value.uid === applicant.uid || applicant.uid === liability.linkToUid) {
              liability.applicantOptions.push(applicant);
            }
            if (applicant.uid === liability.linkToUid) {
              liability.displayedApplicantName = applicant.name;
            }
          });
          // by default in dropdown we need to show whome we have initiated liability, hence we are always updating with initiated borrower uid
          liability.linkToUid = this.xDataServicesForm.controls.borrower.value.uid;
            
          if (liability.uid) {
            this.existingLiabilities.push(liability);
          } else if (
            (liability.writeOffAmount > 0) ||
            (liability.overdueAmount > 0) ||
            (liability.currentOs > 0)
          ) {
            this.activeLiabilities.push(liability);
          } else {
            this.closedLiabilities.push(liability);
          }
        });

        this.activeLiabilities.forEach(ele => ele.linkToUid = this.xDataServicesForm.controls.borrower.value.uid)
        this.closedLiabilities.forEach(ele => ele.linkToUid = this.xDataServicesForm.controls.borrower.value.uid)

        this.creditCardLiabilities = this.activeLiabilities.filter(liability => this.isCreditCard(liability.product));

        this.loanLiabilities = this.activeLiabilities.filter(liability => !this.isCreditCard(liability.product));

        this.selectedExistingLiabilities = this.existingLiabilities.map(() => false);
        this.selectedClosedLiabilities = this.closedLiabilities.map(() => false);
        this.selectedLoanLiabilities = this.loanLiabilities.map(() => true);
        this.selectedCreditCardLiabilities = this.creditCardLiabilities.map(() => true);
        this._snackbar.open("Data retrieved successfully", "Close", {
          duration: 7000,
        });
      },
        (failure) => {
          this.isShowResultCard = false;
          this._snackbar.open(failure, "close", {
            duration: 5000,
          });
        })
    }
  }

  resetValuesToAvoidDuplicate() {
    // Reset values to avoid duplicates on multiple clicks
    this.showError = false;
    this.isShowResultCard = false;
    this.bureauResponse = new BureauResponse();
    this.existingLiabilities = [];
    this.loanLiabilities = [];
    this.creditCardLiabilities = [];
    this.closedLiabilities = [];
    this.selectedExistingLiabilities = [];
    this.selectedClosedLiabilities = [];
    this.selectedLoanLiabilities = [];
    this.selectedCreditCardLiabilities = [];
    this.activeLiabilities = [];
  }


  getBureauReports() {
    this.applicationService.getLiabilitySupportBureau(this.applicationUid).subscribe((response: any) => {
      this.bureauReports = response.body;
    });
  }

  buildForm() {
    // if there is no kycProof captured then we need to display error message
    if (this.listOfLiabilitySupportBureauReport.length === 0) {
      this._snackbar.open("No bureau reports for liability extraction found", "close", {
        duration: 5000,
      });
      return;
    }


    if (this.listOfLiabilitySupportBureauReport.length === 1) {
      this.xDataServicesForm.patchValue({
        reportType: this.listOfLiabilitySupportBureauReport[0].uid,
      });
    }
  }

  onBorrowerSelect(borrower) {
    this.onDocumentNumberOrApplicantChange();
    this.listOfLiabilitySupportBureauReport = this.bureauReports.filter(report =>
      report.linkToUid === borrower.uid && report.supportLiabilityFetch === true && report.bureauReportType === this.serviceProviderCode
    );
    this.buildForm();
  }

  onDocumentNumberOrApplicantChange() {
    this.xDataServicesForm.patchValue({
      reportType: ''
    });
    this.resetValuesToAvoidDuplicate();
  }

  onSubmitClick() {
    // Filter selected liabilities from each category using corresponding selection models
    let selectedExisting = this.existingLiabilities.filter((ele, i) => this.selectedExistingLiabilities[i]);
    let selectedLoan = this.loanLiabilities.filter((ele, i) => this.selectedLoanLiabilities[i]);
    let selectedCreditCard = this.creditCardLiabilities.filter((ele, i) => this.selectedCreditCardLiabilities[i]);
    let selectedClosed = this.closedLiabilities.filter((ele, i) => this.selectedClosedLiabilities[i]);
    let selectedLiabilities = selectedExisting.concat(selectedLoan).concat(selectedCreditCard).concat(selectedClosed);
    let documentUid = this.xDataServicesForm.controls.reportType.value;
    this.applicationService.processLiability(this.applicationUid, documentUid, selectedLiabilities).subscribe((response) => {
      if (response.body.status === 'success') {
        this._snackbar.open(response.body.message, "close", {
          duration: 7000,
        });
        this.isShowResultCard = false;
      }
    });
  }

  // it is used to get total number of selected checkbox 
  get totalSelected(): number {
    // Filter the selectedExistingLiabilities array to include only truthy values (i.e., selected checkboxes)
    // .filter(v => v) removes all falsy values like false, null, undefined, 0, etc.
    // .length gives the count of selected items; || 0 ensures it returns 0 if the array is empty or null
    return (this.selectedExistingLiabilities.filter(v => v).length || 0) +
      (this.selectedLoanLiabilities.filter(v => v).length || 0) +
      (this.selectedCreditCardLiabilities.filter(v => v).length || 0) +
      (this.selectedClosedLiabilities.filter(v => v).length || 0);
  }

  // this is called when a user clicks on an individual card.
  toggleSelection(selectionModel: boolean[], index: number, event: MouseEvent): void {
    selectionModel[index] = !selectionModel[index];
  }

  // this is used to check if all the cards are selected
  // Used to determine the state (checked/unchecked) of the "Select All" checkbox.
  isAllSelected(selectionModel: boolean[]): boolean {
    return selectionModel.length && selectionModel.every(selected => selected);
  }

  // Called when a user clicks the "Select All" checkbox. This is used to select all cards and deselect all
  toggleSelectAll(selectionModel: boolean[], liabilities: any[], event: MatCheckboxChange): void {
    let checked = event.checked;
    for (let i = 0; i < liabilities.length; i++) {
      selectionModel[i] = checked;
    }
  }

  // if creditcard comes in different name in further need to add to this list so there won't be change in html or creditCard format check
  isCreditCard(product: string): boolean {
    if (!product) return false;
    let formattedProductName = product.toLowerCase().replace(/[\s-_]/g, '');
    return ['creditcard', 'cc'].includes(formattedProductName);
  }

  ngOnDestroy(): void { }
}
